import { useMemo, useState } from 'react'
import {
  Stack,
  Title,
  Group,
  Button,
  Loader,
  Alert,
  Table,
  Modal,
  Text,
  TextInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { connectorsApi, type ConnectorItem } from '@/api/connectors'
import { formatApiError } from '@/api/errors'
import { useAuthStore } from '@/store/auth'
import ConnectorRow from './ConnectorRow'
import EmitCertModal, { type TenantOption } from './EmitCertModal'

export default function ConnectorsAdmin() {
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const [emitOpen, setEmitOpen] = useState(false)
  const [revoking, setRevoking] = useState<ConnectorItem | null>(null)
  const [revokeReason, setRevokeReason] = useState('')

  const tenants: TenantOption[] = useMemo(
    () => (user?.tenants ?? []).map((t) => ({ id: t.id, name: t.name })),
    [user],
  )
  const tenantNameById = useMemo(
    () => new Map(tenants.map((t) => [t.id, t.name])),
    [tenants],
  )

  const { data: connectors = [], isLoading, error } = useQuery({
    queryKey: ['connectors', 'admin'],
    queryFn: () => connectorsApi.list(),
  })

  const revokeMutation = useMutation({
    mutationFn: ({ fp, reason }: { fp: string; reason: string }) =>
      connectorsApi.revoke(fp, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['connectors'] })
      notifications.show({
        color: 'teal',
        title: 'Connector revogado',
        message: 'O cert foi marcado como revogado.',
      })
      setRevoking(null)
      setRevokeReason('')
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        title: 'Erro ao revogar',
        message: formatApiError(err, 'Não foi possível revogar o cert.'),
      })
    },
  })

  const renewMutation = useMutation({
    mutationFn: (c: ConnectorItem) => connectorsApi.renew(c.cert_fingerprint),
    onSuccess: (data, c) => {
      qc.invalidateQueries({ queryKey: ['connectors'] })
      // Download cert.pem para entrega offline ao tenant
      const blob = new Blob([data.cert_pem], { type: 'application/x-pem-file' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `connector-${c.tenant_id}-${c.environment}-cert.pem`
      a.click()
      URL.revokeObjectURL(url)
      notifications.show({
        color: 'teal',
        title: 'Cert renovado',
        message: `Novo fingerprint: ${data.fingerprint.slice(0, 12)}…`,
      })
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        title: 'Erro ao renovar',
        message: formatApiError(err, 'Não foi possível renovar o cert.'),
      })
    },
  })

  const handleRenew = (c: ConnectorItem) => {
    const tenantLabel = tenantNameById.get(c.tenant_id) ?? `#${c.tenant_id}`
    if (!window.confirm(
      `Renovar cert do connector ${tenantLabel}/${c.environment}?\n\n`
      + 'Esta ação revoga o cert antigo imediatamente. O connector ficara '
      + 'offline até que o novo cert.pem seja entregue ao tenant.',
    )) return
    renewMutation.mutate(c)
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Connectors</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setEmitOpen(true)}
          data-testid="open-emit-cert"
        >
          Emitir cert
        </Button>
      </Group>

      <Text c="dimmed" size="sm">
        Connectors rodam na VPC do tenant e expoem apps internas via tunnel
        WSS reverso para o broker. 1 connector ativo por (tenant, ambiente).
      </Text>

      {isLoading && <Loader />}
      {error && (
        <Alert color="red" title="Erro ao listar connectors">
          {formatApiError(error, 'Falha ao buscar connectors.')}
        </Alert>
      )}

      {!isLoading && !error && (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tenant</Table.Th>
              <Table.Th>Ambiente</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Fingerprint</Table.Th>
              <Table.Th>Última atividade</Table.Th>
              <Table.Th>Ações</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {connectors.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" ta="center">
                    Nenhum connector emitido. Clique em "Emitir cert".
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {connectors.map((c) => (
              <ConnectorRow
                key={c.cert_fingerprint}
                connector={c}
                tenantName={tenantNameById.get(c.tenant_id)}
                onRevoke={setRevoking}
                onRenew={handleRenew}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}

      <EmitCertModal
        opened={emitOpen}
        onClose={() => setEmitOpen(false)}
        tenants={tenants}
        onIssued={() => qc.invalidateQueries({ queryKey: ['connectors'] })}
      />

      <Modal
        opened={revoking !== null}
        onClose={() => {
          setRevoking(null)
          setRevokeReason('')
        }}
        title="Revogar connector"
      >
        <Stack>
          <Text>
            Tem certeza? Esta ação bloqueia imediatamente o connector
            <Text component="span" fw={700}>
              {' '}
              {revoking?.cert_fingerprint.slice(0, 12)}…
            </Text>
            . Para reativar, será preciso emitir novo cert.
          </Text>
          <TextInput
            label="Motivo (obrigatório para auditoria)"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.currentTarget.value)}
            placeholder="ex: rotate / connector compromised / decommission"
            data-testid="revoke-reason-input"
          />
          <Group justify="end">
            <Button
              variant="default"
              onClick={() => {
                setRevoking(null)
                setRevokeReason('')
              }}
            >
              Cancelar
            </Button>
            <Button
              color="red"
              loading={revokeMutation.isPending}
              disabled={!revokeReason.trim()}
              onClick={() => {
                if (!revoking) return
                revokeMutation.mutate({
                  fp: revoking.cert_fingerprint,
                  reason: revokeReason.trim(),
                })
              }}
              data-testid="revoke-confirm"
            >
              Revogar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
